import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, userState } from "../../features/user/userSlice";
import { extractUserFromToken, getRoleFromRoleId } from "../../uitls/Utils";
import API_CONFIG from "../../config/apiConfig";

const DropdownUser = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const trigger = useRef(null);
  const dropdown = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

 
  const stateOfUser = useSelector(userState);
  const reduxUser = stateOfUser?.user;


  const getLocalStorageUser = () => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("Error parsing user from localStorage:", error);
      return null;
    }
  };

  const localStorageUser = getLocalStorageUser();


  let userData = reduxUser || localStorageUser;

  if (!userData) {
    const token = localStorage.getItem("token") || stateOfUser?.token;
    if (token) {
      console.log("Extracting user from token in DropdownUser...");
      userData = extractUserFromToken(token);
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      }
    }
  }

  userData = userData || {};

  if (userData && !userData.roleName && (userData.userRoleId || userData.roleId)) {
    const roleId = userData.userRoleId || userData.roleId;
    userData.roleName = getRoleFromRoleId(roleId) || "User";
  }


  useEffect(() => {
    if (reduxUser && Object.keys(reduxUser).length > 0) {
      localStorage.setItem("user", JSON.stringify(reduxUser));
    }
  }, [reduxUser]);

  useEffect(() => {
    const updatePosition = () => {
      if (trigger.current && dropdownOpen) {
        const rect = trigger.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          right: window.innerWidth - rect.right,
        });
      }
    };

    if (dropdownOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current || !trigger.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    if (dropdownOpen) {
      document.addEventListener("click", clickHandler);
    }
    return () => document.removeEventListener("click", clickHandler);
  }, [dropdownOpen]);

 
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    if (dropdownOpen) {
      document.addEventListener("keydown", keyHandler);
    }
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/signIn", { replace: true });
  };

  return (
    <div className="relative z-[100]">
      <button
        ref={trigger}
        onClick={(e) => {
          e.stopPropagation();
          setDropdownOpen(!dropdownOpen);
        }}
        className="flex items-center gap-4 cursor-pointer hover:opacity-90 transition-opacity"
        aria-haspopup="menu"
        aria-expanded={dropdownOpen}
        aria-label="User menu"
      >
        <span className="hidden text-right lg:block">
          <span
            className="block text-sm font-semibold text-white capitalize"
          >
            {userData?.firstName} {userData?.lastName}
          </span>
          <span
            className="block text-xs text-white/80"
          >
            {userData?.roleName || "User"}
          </span>
        </span>

        <span className="h-12 w-12 rounded-full overflow-hidden border-2 border-white/20 flex-shrink-0">
          <img
            src={userData?.profileImage ? API_CONFIG.getImageUrl(userData.profileImage) : "/images/user.png"}
            alt="User"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to default image if profile image fails to load
              e.target.src = "/images/user.png";
            }}
          />
        </span>

        <svg
          className={`hidden fill-current sm:block text-white transition-transform duration-200 flex-shrink-0 ${
            dropdownOpen ? "rotate-180" : ""
          }`}
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.410765 0.910734C0.736202 0.585297 1.26384 0.585297 1.58928 0.910734L6.00002 5.32148L10.4108 0.910734C10.7362 0.585297 11.2638 0.585297 11.5893 0.910734C11.9147 1.23617 11.9147 1.76381 11.5893 2.08924L6.58928 7.08924C6.26384 7.41468 5.7362 7.41468 5.41077 7.08924L0.410765 2.08924C0.0853277 1.76381 0.0853277 1.23617 0.410765 0.910734Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {/* <!-- Dropdown Start --> */}
      {dropdownOpen &&
        createPortal(
          <div
            ref={dropdown}
            role="menu"
            aria-hidden={!dropdownOpen}
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setDropdownOpen(false);
                trigger.current?.focus();
              }
            }}
            className="fixed flex w-62.5 flex-col rounded-xl bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)] z-[9999] min-w-[220px] overflow-hidden animate-fadeInUp"
            style={{
              animationDuration: '0.2s',
              animationFillMode: 'forwards',
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
            }}
          >
        <ul
          className="flex flex-col py-2"
          role="none"
        >
          <li role="none">
            <Link
              to="/user-profile"
              role="menuitem"
              tabIndex={dropdownOpen ? 0 : -1}
              className="flex items-center gap-3.5 px-4 py-3 text-sm font-medium text-gray-700 hover:text-[#0b3a5c] hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ease-in-out lg:text-base group"
              onClick={() => setDropdownOpen(false)}
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 group-hover:from-blue-200 group-hover:to-indigo-200 transition-all duration-200">
                <svg
                  className="fill-current text-[#0b3a5c]"
                  width="18"
                  height="18"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 9.62499C8.42188 9.62499 6.35938 7.59687 6.35938 5.12187C6.35938 2.64687 8.42188 0.618744 11 0.618744C13.5781 0.618744 15.6406 2.64687 15.6406 5.12187C15.6406 7.59687 13.5781 9.62499 11 9.62499ZM11 2.16562C9.28125 2.16562 7.90625 3.50624 7.90625 5.12187C7.90625 6.73749 9.28125 8.07812 11 8.07812C12.7188 8.07812 14.0938 6.73749 14.0938 5.12187C14.0938 3.50624 12.7188 2.16562 11 2.16562Z"
                    fill="currentColor"
                  />
                  <path
                    d="M17.7719 21.4156H4.2281C3.5406 21.4156 2.9906 20.8656 2.9906 20.1781V17.0844C2.9906 13.7156 5.7406 10.9656 9.10935 10.9656H12.925C16.2937 10.9656 19.0437 13.7156 19.0437 17.0844V20.1781C19.0094 20.8312 18.4594 21.4156 17.7719 21.4156ZM4.53748 19.8687H17.4969V17.0844C17.4969 14.575 15.4344 12.5125 12.925 12.5125H9.07498C6.5656 12.5125 4.5031 14.575 4.5031 17.0844V19.8687H4.53748Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <span className="flex-1">My Profile</span>
              <svg
                className="w-4 h-4 text-gray-400 group-hover:text-[#0b3a5c] transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </li>
        
        </ul>
        <ul
          className="flex flex-col py-2 border-t border-[#ddd]"
          role="none"
        >
          <li role="none">
            <Link
              to="/user-password"
              role="menuitem"
              tabIndex={dropdownOpen ? 0 : -1}
              className="flex items-center gap-3.5 px-4 py-3 text-sm font-medium text-gray-700 hover:text-[#0b3a5c] hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 ease-in-out lg:text-base group"
              onClick={() => setDropdownOpen(false)}
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-amber-100 to-orange-100 group-hover:from-amber-200 group-hover:to-orange-200 transition-all duration-200">
                <svg
                  className="fill-current text-amber-700"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C9.79 2 8 3.79 8 6V10H6C4.9 10 4 10.9 4 12V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V12C20 10.9 19.1 10 18 10H16V6C16 3.79 14.21 2 12 2ZM10 6C10 4.9 10.9 4 12 4C13.1 4 14 4.9 14 6V10H10V6ZM18 12V20H6V12H18Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <span className="flex-1">Change Password</span>
              <svg
                className="w-4 h-4 text-gray-400 group-hover:text-[#0b3a5c] transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </li>
        </ul>

        <div className="border-t border-[#ddd] pt-2">
          <button
            onClick={handleLogout}
            role="menuitem"
            tabIndex={dropdownOpen ? 0 : -1}
            className="flex items-center gap-3.5 px-4 py-3 w-full text-sm font-medium text-red-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 transition-all duration-200 ease-in-out lg:text-base group rounded-lg mx-2 mb-2"
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-100 group-hover:bg-red-200 transition-all duration-200">
              <svg
                className="fill-current text-red-600 group-hover:text-white"
                width="18"
                height="18"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.5375 0.618744H11.6531C10.7594 0.618744 10.0031 1.37499 10.0031 2.26874V4.64062C10.0031 5.05312 10.3469 5.39687 10.7594 5.39687C11.1719 5.39687 11.55 5.05312 11.55 4.64062V2.23437C11.55 2.16562 11.5844 2.13124 11.6531 2.13124H15.5375C16.3625 2.13124 17.0156 2.78437 17.0156 3.60937V18.3562C17.0156 19.1812 16.3625 19.8344 15.5375 19.8344H11.6531C11.5844 19.8344 11.55 19.8 11.55 19.7312V17.3594C11.55 16.9469 11.2062 16.6031 10.7594 16.6031C10.3125 16.6031 10.0031 16.9469 10.0031 17.3594V19.7312C10.0031 20.625 10.7594 21.3812 11.6531 21.3812H15.5375C17.2219 21.3812 18.5625 20.0062 18.5625 18.3562V3.64374C18.5625 1.95937 17.1875 0.618744 15.5375 0.618744Z"
                  fill="currentColor"
                />
                <path
                  d="M6.05001 11.7563H12.2031C12.6156 11.7563 12.9594 11.4125 12.9594 11C12.9594 10.5875 12.6156 10.2438 12.2031 10.2438H6.08439L8.21564 8.07813C8.52501 7.76875 8.52501 7.2875 8.21564 6.97812C7.90626 6.66875 7.42501 6.66875 7.11564 6.97812L3.67814 10.4844C3.36876 10.7938 3.36876 11.275 3.67814 11.5844L7.11564 15.0906C7.25314 15.2281 7.45939 15.3312 7.66564 15.3312C7.87189 15.3312 8.04376 15.2625 8.21564 15.125C8.52501 14.8156 8.52501 14.3344 8.21564 14.025L6.05001 11.7563Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <span className="flex-1 font-semibold">Log Out</span>
            <svg
              className="w-4 h-4 text-red-400 group-hover:text-white transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        </div>,
          document.body
        )}
      {/* <!-- Dropdown End --> */}
    </div>
  );
};

export default DropdownUser;
